import { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import { ChevronIcon, EditMenuIcon, DownloadMenuIcon, DeleteMenuIcon } from "../common/DocumentRowIcons";
import { DropdownMenu, KebabIcon, MenuItem } from "../common/DropdownMenu";
import { formatDate } from "src/Common/common";
import { downloadImage, downloadPdf, handleError } from "src/Common/function";
import ConfirmDialog from "src/Common/confirmdialog";
import { formatBytes } from "../upload/uploadModalUI";
import DocumentUploadScanModal from "./DocumentUploadScanModal";
import {
  DocumentReviewModal,
  DocumentScanningModal,
  deriveScanResult,
  formatMoney,
  getDocumentDisplayName,
} from "./DocumentScanModals";

const isPdfFile = (fileName) => !!fileName && fileName.toLowerCase().endsWith(".pdf");

export const StatusPill = ({ status }) =>
  status === "CONFIRMED" ? (
    <span className="inline-flex px-2 py-[2px] rounded-[16px] bg-[#DCFCE7] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#15803D]">
      Confirmed
    </span>
  ) : (
    <span className="inline-flex whitespace-nowrap px-2 py-[2px] rounded-[16px] bg-[#FEF9C3] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#A16207]">
      Needs Review
    </span>
  );

/**
 * Collapsible card listing uploaded documents whose totals were extracted by
 * the backend scanner — WO proposals, WO invoices, and PO receipts all render
 * through this one component.
 *
 * Header shows the count and the sum of CONFIRMED amounts (Needs Review rows
 * are excluded, matching the backend's wo_summary / po_summary views). Rows
 * carry the extracted amount, a status pill that reopens review, and a kebab
 * menu.
 *
 * The parent injects the transport and labels; see DocumentUploadScanModal
 * for the uploadFn / scanFn / confirmFn contracts.
 */
function ScannedDocumentsPanel({
  title,
  noun,
  totalLabel,
  documents,
  canManage,
  canDelete,
  accept,
  footer,
  allowRename = true,
  // Hides the Download menu entry — e.g. the PO receipts list only exposes
  // Edit Amount.
  allowDownload = true,
  getFileUrl,
  uploadFn,
  scanFn,
  confirmFn,
  deleteFn,
  onRefresh,
  onView,
  emptyText,
}) {
  const [expanded, setExpanded] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  // { id, anchor } of the row whose kebab menu is open — the anchor element
  // positions the portal-rendered dropdown.
  const [menu, setMenu] = useState(null);

  const hasMenuItems = canManage || allowDownload || canDelete;

  // Review-from-list flow (Needs Review pill / kebab actions).
  const [reviewDocument, setReviewDocument] = useState(null);
  const [reviewResult, setReviewResult] = useState(null);
  const [reviewMode, setReviewMode] = useState("result"); // result | edit
  const [rescanning, setRescanning] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  // Bumped when the review closes so a late re-scan response is ignored.
  const reviewFlowRef = useRef(0);

  const confirmedTotal = useMemo(
    () =>
      documents
        .filter((document) => document.amountStatus === "CONFIRMED" && document.extractedAmount != null)
        .reduce((sum, document) => sum + Number(document.extractedAmount), 0),
    [documents],
  );

  const handleDownload = (document) => {
    if (!document?.fileName) return;
    const downloadName = getDocumentDisplayName(document);
    if (isPdfFile(document.fileName)) {
      downloadPdf(getFileUrl(document), downloadName);
    } else {
      downloadImage(getFileUrl(document), downloadName);
    }
  };

  const handleDelete = async () => {
    const document = documentToDelete;
    setDocumentToDelete(null);
    if (!document?.id) return;
    try {
      await deleteFn(document.id);
      toast(`${noun} deleted successfully`);
      onRefresh?.();
    } catch (error) {
      handleError(error);
    }
  };

  // ---- review / edit amount from the list ----

  const openReview = (document, mode = "result") => {
    setReviewDocument(document);
    setReviewResult(deriveScanResult(document));
    setReviewMode(mode);
  };

  const closeReview = () => {
    reviewFlowRef.current++;
    setReviewDocument(null);
    setReviewResult(null);
    setRescanning(false);
  };

  const handleRescan = async () => {
    const document = reviewDocument;
    if (!document) return;
    const flowId = ++reviewFlowRef.current;
    setRescanning(true);
    try {
      const res = await scanFn(document.id);
      if (reviewFlowRef.current !== flowId) return;
      setReviewDocument(res.data?.document || document);
      setReviewResult(res.data);
      setReviewMode("result");
    } catch (error) {
      if (reviewFlowRef.current !== flowId) return;
      setReviewResult({ status: "NOT_FOUND", best: null, candidates: [] });
    } finally {
      if (reviewFlowRef.current === flowId) setRescanning(false);
    }
  };

  const handleConfirmAmount = async (amount, sourcePage, sourceLabel) => {
    if (!reviewDocument) return;
    setConfirmBusy(true);
    try {
      await confirmFn(reviewDocument.id, amount, sourcePage, sourceLabel);
      toast(`${noun} amount confirmed`);
      closeReview();
      onRefresh?.();
    } catch (error) {
      handleError(error);
    } finally {
      setConfirmBusy(false);
    }
  };

  return (
    <div className="w-full bg-white rounded-[12px] overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-[#E5E8F3]">
      {/* Header */}
      <div
        onClick={() => setExpanded((prev) => !prev)}
        className="w-full px-6 py-4 flex items-center justify-between bg-[#F8F9FE] border-b border-[#E5E8F3] cursor-pointer"
      >
        <div className="flex items-center gap-4 min-w-0">
          <div className="flex items-center gap-3">
            <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-[#1A2332]">
              {title}
            </span>
            <span className="min-w-[24px] px-2 py-[2px] flex items-center justify-center rounded-[16px] bg-[#EEF0F9] font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#5F6477]">
              {documents.length}
            </span>
          </div>
          {documents.length > 0 && (
            <>
              <span className="w-px h-4 bg-[#E5E8F3]" />
              <span className="flex items-center gap-2 min-w-0">
                <span className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#6A7282]">
                  Total:
                </span>
                <span className="font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] text-[#1A2332]">
                  {formatMoney(confirmedTotal)}
                </span>
                <img
                  src="/images/common/tooltip.png"
                  alt="info"
                  data-tooltip-id={`${title}-total-tooltip`}
                  data-tooltip-html={`Sum of confirmed ${noun.toLowerCase()} amounts.<br/>${noun}s marked Needs Review are not included.`}
                  className="w-3 h-3"
                  onClick={(e) => e.stopPropagation()}
                />
                <Tooltip id={`${title}-total-tooltip`} positionStrategy="fixed" />
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-4">
          {canManage && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setShowUploadModal(true);
              }}
              className="h-[32px] px-3 rounded-[8px] bg-[#0065FD] flex items-center justify-center gap-2 font-OpenSansSemiBold text-[14px] leading-[20px] tracking-[0.14px] capitalize text-white cursor-pointer"
            >
              Upload
            </button>
          )}
          <ChevronIcon expanded={expanded} />
        </div>
      </div>

      <div
        className={`w-full grid transition-[grid-template-rows] duration-300 ease-in-out ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div className="w-full min-h-0 overflow-hidden">
          {documents.length === 0 ? (
            <div className="w-full flex items-center px-6 h-[64px]">
              <span className="font-OpenSansRegular text-[14px] leading-[20px] text-[#5F6477]">
                {emptyText || `No ${title.toLowerCase()}`}
              </span>
            </div>
          ) : (
            <div className="w-full flex flex-col">
              {/* Column headers */}
              <div className="w-full flex items-center h-[44px] bg-[#FBFCFF] border-b border-[#E5E8F3]">
                <span className="flex-1 min-w-0 px-3 font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
                  File Name
                </span>
                <span className="w-[100px] shrink-0 px-3 font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
                  Extracted
                </span>
                <span className="w-[120px] shrink-0 px-3 font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] capitalize text-[#5F6477]">
                  Status
                </span>
                <span className="w-[52px] shrink-0" />
              </div>

              {documents.map((document) => {
                const displayName = getDocumentDisplayName(document);
                const isConfirmed = document.amountStatus === "CONFIRMED";
                const subParts = [
                  document.fileSize ? formatBytes(document.fileSize) : null,
                  document.uploaderName || null,
                  document.createdAt ? formatDate(document.createdAt, "MMM D, YYYY") : null,
                ].filter(Boolean);
                return (
                  <div
                    key={document.id}
                    className="w-full flex items-center h-[62px] border-b border-[#E5E8F3] last:border-b-0"
                  >
                    <div
                      className="flex-1 min-w-0 px-3 flex flex-col cursor-pointer"
                      onClick={() => onView?.(document)}
                    >
                      <span
                        className="font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#1A2332] truncate"
                        title={displayName}
                      >
                        {displayName}
                      </span>
                      <span className="font-OpenSansRegular text-[12px] leading-[18px] tracking-[0.12px] text-[#7C829A] truncate">
                        {subParts.join(" • ") || "-"}
                      </span>
                    </div>

                    <span className="w-[100px] shrink-0 px-3 font-OpenSansSemiBold text-[12px] leading-[18px] tracking-[0.12px] text-[#1A2332]">
                      {document.extractedAmount != null ? formatMoney(document.extractedAmount) : "—"}
                    </span>

                    <span className="w-[120px] shrink-0 px-3">
                      <button
                        type="button"
                        className={canManage && !isConfirmed ? "cursor-pointer" : "cursor-default"}
                        onClick={() => canManage && !isConfirmed && openReview(document)}
                        title={canManage && !isConfirmed ? "Review the extracted amount" : undefined}
                      >
                        <StatusPill status={document.amountStatus} />
                      </button>
                    </span>

                    <span className="w-[52px] shrink-0 pr-3 flex justify-end">
                      {hasMenuItems && (
                        <button
                          type="button"
                          onClick={(e) =>
                            setMenu(menu?.id === document.id ? null : { id: document.id, anchor: e.currentTarget })
                          }
                          className="p-2.5 rounded-[8px] text-[#5F6477] hover:bg-[#F1F3FA] cursor-pointer"
                          aria-label={`${noun} actions`}
                        >
                          <KebabIcon />
                        </button>
                      )}
                      {menu?.id === document.id && (
                        <DropdownMenu anchorEl={menu.anchor} onClose={() => setMenu(null)}>
                          {canManage && !isConfirmed && (
                            <MenuItem
                              icon={<EditMenuIcon />}
                              onClick={() => {
                                setMenu(null);
                                openReview(document);
                              }}
                            >
                              Review Amount
                            </MenuItem>
                          )}
                          {canManage && isConfirmed && (
                            <MenuItem
                              icon={<EditMenuIcon />}
                              onClick={() => {
                                setMenu(null);
                                openReview(document, "edit");
                              }}
                            >
                              Edit Amount
                            </MenuItem>
                          )}
                          {allowDownload && (
                            <MenuItem
                              icon={<DownloadMenuIcon />}
                              onClick={() => {
                                setMenu(null);
                                handleDownload(document);
                              }}
                            >
                              Download
                            </MenuItem>
                          )}
                          {canDelete && (
                            <MenuItem
                              danger
                              icon={<DeleteMenuIcon />}
                              onClick={() => {
                                setMenu(null);
                                setDocumentToDelete(document);
                              }}
                            >
                              Delete
                            </MenuItem>
                          )}
                        </DropdownMenu>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        title={`DELETE ${noun.toUpperCase()}`}
        description={`Are you sure you want to delete this ${noun.toLowerCase()}?`}
        shown={!!documentToDelete}
        onConfirmClicked={handleDelete}
        onCancelClicked={() => setDocumentToDelete(null)}
      />

      <DocumentUploadScanModal
        shown={showUploadModal}
        noun={noun}
        totalLabel={totalLabel}
        accept={accept}
        footer={footer}
        allowRename={allowRename}
        getFileUrl={getFileUrl}
        uploadFn={uploadFn}
        scanFn={scanFn}
        confirmFn={confirmFn}
        onClose={() => setShowUploadModal(false)}
        onUploaded={onRefresh}
      />

      {rescanning && reviewDocument && (
        <DocumentScanningModal
          noun={noun}
          fileLabel={getDocumentDisplayName(reviewDocument)}
          onCancel={closeReview}
        />
      )}

      {!rescanning && reviewDocument && reviewResult && (
        <DocumentReviewModal
          document={reviewDocument}
          noun={noun}
          totalLabel={totalLabel}
          getFileUrl={getFileUrl}
          result={reviewResult}
          busy={confirmBusy}
          initialMode={reviewMode}
          onConfirm={handleConfirmAmount}
          onClose={closeReview}
          onRescan={handleRescan}
        />
      )}
    </div>
  );
}

export default ScannedDocumentsPanel;
